'use client';

import { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { PrefetchKind } from 'next/dist/client/components/router-reducer/router-reducer-types';
import { useAuth } from './AuthProvider';
import { createRouteIntents, prefetchDestination } from '@/lib/navigation-prefetch';

/** Delegation includes server-rendered links, plain anchors and future components. */
export default function NavigationPrefetch() {
  const router = useRouter();
  const { user, loading } = useAuth();
  useEffect(() => {
    if (loading) return;
    const connection = (navigator as Navigator & {connection?:{saveData?:boolean;effectiveType?:string}}).connection;
    const intents = createRouteIntents();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const anchor = (target: EventTarget | null) => target instanceof Element ? target.closest<HTMLAnchorElement>('a[href]') : null;
    const warm = (link: HTMLAnchorElement) => {
      if (!navigator.onLine || connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType || '')) return;
      if (link.hasAttribute('download') || link.getAttribute('aria-disabled') === 'true' || link.dataset.prefetch === 'false' || (link.target && link.target !== '_self')) return;
      const url = prefetchDestination(link.href, window.location.href);
      if (!url) return;
      const href = url.pathname + url.search;
      if (intents.claim(href)) {
        // Explicit intent warrants the full route, including dynamic segments.
        try { router.prefetch(href, {kind:PrefetchKind.FULL,onInvalidate:()=>intents.invalidate(href)}); } catch { intents.invalidate(href); }
      }
    };
    const cancel = () => { if (timer) clearTimeout(timer); timer=undefined; };
    const hover = (event: PointerEvent) => {
      if (event.pointerType === 'touch') return;
      const link = anchor(event.target);
      if (!link || (event.relatedTarget instanceof Node && link.contains(event.relatedTarget))) return;
      cancel(); timer=setTimeout(()=>warm(link),80);
    };
    const leave = (event: PointerEvent) => {
      const link = anchor(event.target);
      if (link && !(event.relatedTarget instanceof Node && link.contains(event.relatedTarget))) cancel();
    };
    const focus = (event: FocusEvent) => { const link=anchor(event.target); if(link){cancel();warm(link);} };
    const touch = (event: PointerEvent) => { const link=anchor(event.target); if(link && event.pointerType==='touch') warm(link); };
    document.addEventListener('pointerover',hover);
    document.addEventListener('pointerout',leave);
    document.addEventListener('focusin',focus);
    document.addEventListener('pointerdown',touch);
    return () => { cancel(); document.removeEventListener('pointerover',hover); document.removeEventListener('pointerout',leave); document.removeEventListener('focusin',focus); document.removeEventListener('pointerdown',touch); };
  }, [router,user?.id,loading]);
  return null;
}
